import React, { useState, useRef, useEffect } from 'react';
import { 
  Bot, X, Send, Loader2, Sparkles, Settings2, ChevronDown,
  MessageSquare, Trash2, Plus, Check, Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Model categories with icons
const MODEL_CATEGORIES = {
  gpt: { name: 'OpenAI GPT', color: 'bg-emerald-500' },
  claude: { name: 'Anthropic Claude', color: 'bg-orange-500' },
  gemini: { name: 'Google Gemini', color: 'bg-blue-500' },
  llama: { name: 'Meta Llama', color: 'bg-purple-500' },
  other: { name: 'Other Models', color: 'bg-gray-500' },
};

// Quick prompts for options trading
const QUICK_PROMPTS = [
  { label: "Compare strategies", prompt: "Compare iron condor vs butterfly spread for neutral market outlook" },
  { label: "Risk analysis", prompt: "What are the key risks of selling naked puts?" },
  { label: "Greeks explained", prompt: "Explain how theta decay affects different option strategies" },
  { label: "Market analysis", prompt: "What factors should I consider before entering an options trade?" },
];

const PoeChat = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedModel, setSelectedModel] = useState('GPT-4o');
  const [availableModels, setAvailableModels] = useState([]);
  const [categorizedModels, setCategorizedModels] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(
    "You are an expert options trading advisor. Provide clear, actionable advice on options strategies, risk management, and market analysis. Always consider the user's risk tolerance and investment goals."
  );
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Fetch available models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(`${API}/poe/models`);
        const data = await response.json();
        
        if (data.models) {
          setAvailableModels(data.models);
        }
        if (data.categorized) {
          setCategorizedModels(data.categorized);
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
        // Use fallback models
        setCategorizedModels({
          gpt: [
            { id: 'GPT-4o', name: 'GPT-4o' },
            { id: 'GPT-4o-Mini', name: 'GPT-4o Mini' },
          ],
          claude: [
            { id: 'Claude-3.5-Sonnet', name: 'Claude 3.5 Sonnet' },
          ],
          gemini: [
            { id: 'Gemini-2.0-Flash', name: 'Gemini 2.0 Flash' },
          ],
        });
      }
    };
    
    if (isOpen) {
      fetchModels();
    }
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `**Welcome to Poe AI Chat!** 🤖\n\nI have access to **100+ AI models** including GPT-4o, Claude, Gemini, Llama, and more. I'm configured as your options trading advisor.\n\nCurrent model: **${selectedModel}**\n\nAsk me anything about options strategies, market analysis, or trading concepts!`,
        timestamp: new Date(),
        model: selectedModel
      }]);
    }
  }, [isOpen]);

  const sendMessage = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading || isStreaming) return;

    const userMessage = {
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsStreaming(true);

    // Add placeholder for assistant message
    const assistantPlaceholder = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      model: selectedModel,
      isStreaming: true
    };
    setMessages(prev => [...prev, assistantPlaceholder]);

    try {
      // Create abort controller for cancellation
      abortControllerRef.current = new AbortController();
      
      // Build messages for API
      const apiMessages = messages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));
      apiMessages.push({ role: 'user', content: messageText.trim() });

      const response = await fetch(`${API}/poe/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: apiMessages,
          model: selectedModel,
          temperature: 0.7,
          max_tokens: 4096,
          stream: true,
          system_message: systemPrompt
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                fullContent += data.content;
                // Update the last message with streaming content
                setMessages(prev => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
                    updated[lastIdx] = {
                      ...updated[lastIdx],
                      content: fullContent
                    };
                  }
                  return updated;
                });
              }
              if (data.done) {
                // Finalize the message
                setMessages(prev => {
                  const updated = [...prev];
                  const lastIdx = updated.length - 1;
                  if (lastIdx >= 0) {
                    updated[lastIdx] = {
                      ...updated[lastIdx],
                      isStreaming: false
                    };
                  }
                  return updated;
                });
              }
              if (data.error) {
                throw new Error(data.error);
              }
            } catch (e) {
              // Ignore JSON parse errors for incomplete chunks
              if (e.message && !e.message.includes('JSON')) {
                throw e;
              }
            }
          }
        }
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled - update message to show it was stopped
        setMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (lastIdx >= 0 && updated[lastIdx].role === 'assistant') {
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: updated[lastIdx].content + '\n\n*[Response stopped by user]*',
              isStreaming: false
            };
          }
          return updated;
        });
      } else {
        console.error('Poe chat error:', error);
        // Remove placeholder and add error message
        setMessages(prev => {
          const filtered = prev.filter(m => !m.isStreaming || m.content);
          return [...filtered, {
            role: 'assistant',
            content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
            timestamp: new Date(),
            model: selectedModel,
            isError: true
          }];
        });
        toast.error('Failed to get response from Poe');
      }
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: `Chat cleared. Ready to help with your options trading questions!\n\nCurrent model: **${selectedModel}**`,
      timestamp: new Date(),
      model: selectedModel
    }]);
  };

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Get model color based on ID
  const getModelColor = (modelId) => {
    if (modelId?.toLowerCase().includes('gpt') || modelId?.toLowerCase().includes('o1')) return 'bg-emerald-500';
    if (modelId?.toLowerCase().includes('claude')) return 'bg-orange-500';
    if (modelId?.toLowerCase().includes('gemini')) return 'bg-blue-500';
    if (modelId?.toLowerCase().includes('llama')) return 'bg-purple-500';
    return 'bg-gray-500';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold">Poe AI Research</DialogTitle>
                <p className="text-xs text-muted-foreground">100+ AI models at your fingertips</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Model Selector */}
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger className="w-[180px] h-9">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${getModelColor(selectedModel)}`} />
                    <SelectValue placeholder="Select model" />
                  </div>
                </SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  {Object.entries(categorizedModels).map(([category, models]) => (
                    <SelectGroup key={category}>
                      <SelectLabel className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${MODEL_CATEGORIES[category]?.color || 'bg-gray-500'}`} />
                        {MODEL_CATEGORIES[category]?.name || category}
                      </SelectLabel>
                      {models.map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          {model.name || model.id}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSettings(!showSettings)}
                className="h-9 w-9"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={clearChat}
                className="h-9 w-9"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Settings Panel */}
          {showSettings && (
            <div className="mt-3 p-3 bg-muted rounded-lg">
              <label className="text-sm font-medium mb-2 block">System Prompt</label>
              <Textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Set the AI's behavior..."
                className="min-h-[80px] text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This sets the AI's role and behavior for all responses.
              </p>
            </div>
          )}
        </DialogHeader>

        {/* Quick Prompts */}
        {messages.length <= 1 && (
          <div className="px-4 py-3 border-b flex-shrink-0">
            <p className="text-sm text-muted-foreground mb-2">Quick prompts:</p>
            <div className="flex flex-wrap gap-2">
              {QUICK_PROMPTS.map((item, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => sendMessage(item.prompt)}
                  className="text-xs"
                >
                  <Sparkles className="h-3 w-3 mr-1" />
                  {item.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Messages Area */}
        <ScrollArea className="flex-1 px-4 py-4">
          <div className="space-y-4 max-w-3xl mx-auto">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : message.isError
                      ? 'bg-destructive/10 border border-destructive/20'
                      : 'bg-muted'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="text-xs">
                        <div className={`w-1.5 h-1.5 rounded-full mr-1 ${getModelColor(message.model)}`} />
                        {message.model || selectedModel}
                      </Badge>
                      {message.isStreaming && (
                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  )}
                  
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">
                      {message.content || (message.isStreaming ? '...' : '')}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
                    <span className="text-xs text-muted-foreground">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </span>
                    {message.role === 'assistant' && message.content && !message.isStreaming && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => copyMessage(message.content)}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="px-4 py-3 border-t bg-background flex-shrink-0">
          <div className="flex gap-2 max-w-3xl mx-auto">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Ask ${selectedModel} anything...`}
              disabled={isLoading && !isStreaming}
              className="flex-1"
            />
            {isStreaming ? (
              <Button
                onClick={stopStreaming}
                variant="destructive"
                className="px-4"
              >
                <X className="h-4 w-4 mr-1" />
                Stop
              </Button>
            ) : (
              <Button
                onClick={() => sendMessage()}
                disabled={!inputValue.trim() || isLoading}
                className="px-4"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-1" />
                    Send
                  </>
                )}
              </Button>
            )}
          </div>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Powered by Poe.com • {availableModels.length || '100+'}  models available
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PoeChat;
